import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Drawer, Paper, Typography, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Collapse, TextField, Avatar, List, ListItemButton, ListItemAvatar, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ApplicationFormModal } from '../../../components/Modals/ApplicationFormModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useSchools } from '../../../hooks/useSchools';
import { useTeacher } from '../../../hooks/useTeacher';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSessionClass } from 'contexts/SessionClassContext';
import { useTranslation } from 'react-i18next';
import EnhancedTable from '../../../components/common/EnhancedTable';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTenantHeader } from '../../../hooks/useTenantHeader';
import { useError } from '../../../contexts/ErrorContext';
import StudentHistoryModal from '../../../components/Modals/StudentHistoryModal';
import StudentDataSheetSortModal from '../../../components/Modals/StudentDataSheetSortModal';
import ScholarNumberModal from '../../../components/Modals/ScholarNumberModal';
import { useStudents, ApplicationStudentDTO, StudentRollScholarUpdateDto, AgeWiseStudentRequestDto, AdvanceStudentSearchResponseDto } from '../../../hooks/useStudents';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { ApplicationFormData } from '../../../components/Modals/ApplicationFormModal';
import { format } from 'date-fns';
import { useMedium } from '../../../components/layout/Layout';
import { useMarkSheets, MarksheetResponseDto } from '../../../hooks/useMarkSheets';
import MarksheetTemplate from '../../../components/templates/MarksheetTemplate';
import ApplicationFormTemplate from '../../../components/templates/ApplicationFormTemplate';
import FeeReceiptTemplate from '../../../components/templates/FeeReceiptTemplate';
import { FeeReceipt } from '../../../services/paymentService';
import Tooltip from '@mui/material/Tooltip';
import { useClasses } from '../../../hooks/useClasses';
import { useStudentUpdateRequest } from '../../../hooks/useStudentUpdateRequest';

const StudentManagement: React.FC = () => {
  const { selectedSession, selectedClass } = useSessionClass();
  const { t } = useTranslation();
  const { tenantLoading } = useTenantHeader();
  const { user, isSchoolAdmin, isTeacher } = useAuth();
  const { showError, showSuccess } = useError();
  const schoolId = user?.schoolId;
  const { useSchoolById } = useSchools();
  const { classesQuery } = useClasses(schoolId || undefined);
  const { teachersQuery } = useTeacher(undefined, tenantLoading);
  const { data: school } = useSchoolById(schoolId || undefined);
  const { selectedMedium } = useMedium();

  const {
    updateStudentWithDTOMutation,
    deleteStudentMutation,
    downloadStudentsMutation,
    updateStudentUserStatusMutation,
    updateRollNumberAndScholarNumberMutation,
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    getAgeWiseStudentsMutation,
    advanceStudentSearch,
    useSiblingGroups,
  } = useStudents(tenantLoading, schoolId || undefined);

  const siblingGroupsQuery = useSiblingGroups();
  
  const classIdFilter =
    typeof selectedClass?.id === "number" && selectedClass.id > 0
      ? selectedClass.id
      : undefined;

  const classNameFilter =
    selectedClass?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : selectedClass?.section === "__GRADE_ALL__"
        ? `${String(selectedClass?.name || "").trim()}-ALL`
        : (String(selectedClass?.name || "").includes("-ALL") ? String(selectedClass?.name || "") : undefined);

  const marksheetsClassIdParam = classNameFilter ? undefined : classIdFilter;
  const marksheetsClassNameParam = classNameFilter ? classNameFilter : undefined;

  // Fetch students by sessionId + (classId OR className for *-ALL / ALL)
  const studentsBySessionIdAndClassIdQuery = useStudentsBySessionIdAndClassId(
    selectedSession?.id,
    classIdFilter
  );

  const studentsBySessionIdAndClassNameQuery = useStudentsBySessionIdAndClassName(
    selectedSession?.id,
    classNameFilter
  );

  const studentsQuery =
    classNameFilter ? studentsBySessionIdAndClassNameQuery : studentsBySessionIdAndClassIdQuery;
  
  // Use the new query's loading and error states
  const isLoading = studentsQuery.isLoading;
  const error = studentsQuery.error;
  const refetch = studentsQuery.refetch;
  const { marksheetsQuery } = useMarkSheets(
    tenantLoading,
    false,
    marksheetsClassIdParam,
    selectedSession?.id,
    undefined,
    undefined,
    false,
    marksheetsClassNameParam
  );
  const queryClient = useQueryClient();

  // Clear students cache when schoolId changes to prevent cross-school data
  useEffect(() => {
    if (schoolId) {
      // Invalidate students queries when schoolId changes
      queryClient.invalidateQueries({
        queryKey: ["students", schoolId],
      });
      // Also invalidate studentClassMapping queries
      queryClient.invalidateQueries({
        queryKey: ["studentClassMapping"],
      });
    }
  }, [schoolId, queryClient]);


  const [modalOpen, setModalOpen] = useState(false);
  const [siblingListOpen, setSiblingListOpen] = useState(false);
  const [siblingSearchQuery, setSiblingSearchQuery] = useState('');
  const [showCurrentClassOnly, setShowCurrentClassOnly] = useState(false);

  // Memoized sibling table processing
  const siblingTableRows = useMemo(() => {
    const siblingGroups = siblingGroupsQuery.data;
    if (!siblingGroups) return [];

    const rows: Array<{
      studentId: number;
      studentName: string;
      className: string;
      section: string;
      rollNumber: string;
      parentMobile: string;
      loginAccountName: string;
      siblingsList: Array<{
        studentName: string;
        className: string;
        section: string;
      }>;
    }> = [];

    siblingGroups.forEach(group => {
      // Find the sibling with the minimum studentId in the group (this is the sibling through which they will login)
      const loginSibling = group.siblings.reduce((min, s) => s.studentId < min.studentId ? s : min, group.siblings[0]);
      const loginAccountName = loginSibling ? loginSibling.studentName : '';

      group.siblings.forEach(student => {
        const selectedClassNameBase = selectedClass?.name ? selectedClass.name.split('-')[0].trim().toLowerCase() : '';
        const isCurrentClass = selectedClass && (
          student.className.trim().toLowerCase() === selectedClassNameBase
        );

        if (showCurrentClassOnly && !isCurrentClass) {
          return;
        }

        const siblings = group.siblings.filter(s => s.studentId !== student.studentId);

        rows.push({
          studentId: student.studentId,
          studentName: student.studentName,
          className: student.className,
          section: student.section,
          rollNumber: student.rollNumber,
          parentMobile: group.parentMobile,
          loginAccountName: loginAccountName,
          siblingsList: siblings.map(s => ({
            studentName: s.studentName,
            className: s.className,
            section: s.section
          }))
        });
      });
    });

    if (siblingSearchQuery.trim()) {
      const query = siblingSearchQuery.toLowerCase().trim();
      return rows.filter(row => 
        row.studentName.toLowerCase().includes(query) ||
        row.parentMobile.toLowerCase().includes(query) ||
        row.loginAccountName.toLowerCase().includes(query) ||
        row.siblingsList.some(s => s.studentName.toLowerCase().includes(query))
      );
    }

    return rows;
  }, [siblingGroupsQuery.data, selectedClass, showCurrentClassOnly, siblingSearchQuery]);

  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentStudent, setCurrentStudent] = useState<any>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyStudentId, setHistoryStudentId] = useState<number | null>(null);
  const [historyStudentName, setHistoryStudentName] = useState<string>('');
  const [historyStudentRollNo, setHistoryStudentRollNo] = useState<string>('');
  const [scholarNumberModalOpen, setScholarNumberModalOpen] = useState(false);
  const [profileStatusDialogOpen, setProfileStatusDialogOpen] = useState(false);
  const [studentToUpdateStatus, setStudentToUpdateStatus] = useState<{ id: number; userStatus: boolean } | null>(null);
  const [teacherConfirmOpen, setTeacherConfirmOpen] = useState(false);
  const [pendingTeacherFormData, setPendingTeacherFormData] = useState<ApplicationFormData | null>(null);
  const [sentRequestsOpen, setSentRequestsOpen] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState<number | null>(null);
  const [ageFilterDialogOpen, setAgeFilterDialogOpen] = useState(false);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [ageFilteredStudents, setAgeFilteredStudents] = useState<any[] | null>(null);
  const [advanceSearchDialogOpen, setAdvanceSearchDialogOpen] = useState(false);
  const [advanceSearchResult, setAdvanceSearchResult] = useState<AdvanceStudentSearchResponseDto | null>(null);
  const [advanceSearchLoading, setAdvanceSearchLoading] = useState(false);
  const [advanceSearchSelectedStudent, setAdvanceSearchSelectedStudent] = useState<any | null>(null);
  const [advanceViewApplicationOpen, setAdvanceViewApplicationOpen] = useState(false);
  const [advanceViewMarksheet, setAdvanceViewMarksheet] = useState<MarksheetResponseDto | null>(null);
  const [advanceViewFeeReceipt, setAdvanceViewFeeReceipt] = useState<FeeReceipt | null>(null);

  const handleAgeFilterSubmit = async () => {
    if (!selectedClass || !selectedSession) {
      showError(t('please_select_class_and_session'));
      return;
    }
    // When ALL sections selected → send className (e.g. "9th"), classId = 0
    // When specific section selected (A/B/...) → send classId, className = ""
    const isAllClass = !!classNameFilter;
    const ageClassName = isAllClass
      ? String(classNameFilter).replace(/-ALL$/i, '').trim()
      : '';
    const ageClassId = isAllClass ? 0 : (selectedClass.id ?? 0);

    try {
      const result = await getAgeWiseStudentsMutation.mutateAsync({
        className: ageClassName,
        classId: ageClassId,
        sessionId: selectedSession.id!,
        minAge: Number(minAge),
        maxAge: maxAge !== '' ? Number(maxAge) : null,
      });
      setAgeFilteredStudents(result);
      setAgeFilterDialogOpen(false);
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Failed to fetch age-wise students');
    }
  };

  const handleClearAgeFilter = () => {
    setAgeFilteredStudents(null);
    setMinAge('');
    setMaxAge('');
  };

  useEffect(() => {
    handleClearAgeFilter();
  }, [selectedClass?.id, selectedClass?.section]);

  const getStudentInitial = (name: string) => (name?.trim()?.[0] || '?').toUpperCase();

  const handleCloseAdvanceSearch = () => {
    setAdvanceSearchDialogOpen(false);
    setAdvanceSearchResult(null);
    setAdvanceSearchSelectedStudent(null);
    setAdvanceSearchLoading(false);
    setAdvanceViewApplicationOpen(false);
    setAdvanceViewMarksheet(null);
    setAdvanceViewFeeReceipt(null);
  };

  const handleClearAdvanceSelection = () => {
    setAdvanceSearchSelectedStudent(null);
    setAdvanceSearchResult(null);
    setAdvanceSearchLoading(false);
  };

  const handleAdvanceSearchSelect = async (student: any) => {
    const code = student?.studentCode?.trim();
    const classId = student?.schoolClassId;
    const sessionId = student?.sessionId ?? selectedSession?.id;
    if (!code) {
      showError('Student code not found');
      return;
    }
    if (!classId || !sessionId) {
      showError(t('please_select_class_and_session'));
      return;
    }
    setAdvanceSearchSelectedStudent(student);
    setAdvanceSearchLoading(true);
    setAdvanceSearchResult(null);
    try {
      const result = await advanceStudentSearch(code, classId, sessionId);
      setAdvanceSearchResult(result);
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Advance search failed');
    } finally {
      setAdvanceSearchLoading(false);
    }
  };

  const handleAdvanceViewFeeReceipt = (receipt: any) => {
    const feeTx = receipt.feeTransactionDto || {};
    const acct = feeTx.studentAccountsResponseDto;
    const feeTransactionMapped = {
      ...feeTx,
      studentAccount: acct
        ? {
            id: acct.id,
            studentAccountNumber: acct.studentAccountNumber,
            student: acct.student,
          }
        : feeTx.studentAccount,
    };
    setAdvanceViewFeeReceipt({
      id: receipt.id,
      localDateTime: receipt.localDateTime,
      feeTransaction: feeTransactionMapped,
      particulars: receipt.feeParticulars,
    } as FeeReceipt);
  };

  const advanceViewButtonSx = {
    textTransform: 'none' as const,
    borderRadius: 10,
    boxShadow: 'none',
    bgcolor: '#3092e3',
    px: 2,
    '&:hover': { boxShadow: 'none', bgcolor: 'primary.main' },
  };

  useEffect(() => {
    if (advanceSearchDialogOpen) {
      setAdvanceSearchResult(null);
      setAdvanceSearchSelectedStudent(null);
      setAdvanceSearchLoading(false);
    }
  }, [selectedClass?.id, selectedClass?.section, selectedSession?.id]);

  const { createStudentUpdateRequestMutation, useStudentUpdateRequests } = useStudentUpdateRequest();
  const { data: sentRequests, isLoading: sentRequestsLoading } = useStudentUpdateRequests(isTeacher());

  const groupedSentRequests = useMemo(() => {
    if (!sentRequests) return [];
    const map = new Map<number, any>();
    for (const req of sentRequests) {
      const key = req.studentId;
      if (!key) continue;
      if (map.has(key)) {
        const existing = map.get(key)!;
        existing.changes = [...(existing.changes || []), ...(req.changes || [])];
      } else {
        map.set(key, { ...req, changes: [...(req.changes || [])] });
      }
    }
    return Array.from(map.values());
  }, [sentRequests]);

  const teacherFieldsToCompare = [
    { key: 'studentName', label: 'Student Name' },
    { key: 'fatherName', label: 'Father Name' },
    { key: 'motherName', label: 'Mother Name' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'religion', label: 'Religion' },
    { key: 'caste', label: 'Caste' },
    { key: 'subCaste', label: 'Sub Caste' },
    { key: 'casteCertificate', label: 'Caste Certificate' },
    { key: 'aadharNo', label: 'Aadhar No' },
    { key: 'sssmid', label: 'SSSMID' },
    { key: 'penNo', label: 'PEN No' },
    { key: 'apparId', label: 'APPAR ID' },
    { key: 'scholarNo', label: 'Scholar No' },
    { key: 'houseNo', label: 'House No' },
    { key: 'street', label: 'Street' },
    { key: 'landmark', label: 'Landmark' },
    { key: 'area', label: 'Area' },
    { key: 'city', label: 'City/Village' },
    { key: 'postOffice', label: 'Post Office' },
    { key: 'district', label: 'District' },
    { key: 'state', label: 'State' },
    { key: 'pinCode', label: 'Pin Code' },
    { key: 'mobileNo', label: 'Mobile No' },
    { key: 'gmail', label: 'Email' },
    { key: 'bankName', label: 'Bank Name' },
    { key: 'ifcCode', label: 'IFSC Code' },
    { key: 'accountNo', label: 'Account No' },
    { key: 'annualIncome', label: 'Annual Income' },
    { key: 'admissionDate', label: 'Admission Date' },
    { key: 'admissionClass', label: 'Admission Class' },
    { key: 'section', label: 'Section' },
    { key: 'stream', label: 'Stream' },
    { key: 'medium', label: 'Medium' },
    { key: 'subjects', label: 'Subjects' },
    { key: 'house', label: 'House' },
  ];

  const DATE_KEYS = new Set(['dateOfBirth', 'admissionDate']);
  const CASE_INSENSITIVE_KEYS = new Set(['admissionClass']);
  const ARRAY_KEYS = new Set(['subjects']);

  const normalizeDateForComparison = (val: string): string => {
    const s = val.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return `${iso[1]}${iso[2]}${iso[3]}`;
    const dmy = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
    if (dmy) return `${dmy[3]}${dmy[2]}${dmy[1]}`;
    return s;
  };

  const getChangedTeacherFields = (initial: ApplicationFormData, updated: ApplicationFormData) =>
    teacherFieldsToCompare
      .filter(({ key }) => {
        if (ARRAY_KEYS.has(key)) {
          const toArr = (v: any) => (Array.isArray(v) ? v as string[] : String(v || '').split(',').map((s: string) => s.trim())).filter(Boolean).sort();
          return toArr((initial as any)[key]).join(',') !== toArr((updated as any)[key]).join(',');
        }
        const oldVal = String((initial as any)[key] || '').trim();
        const newVal = String((updated as any)[key] || '').trim();
        if (DATE_KEYS.has(key)) {
          return normalizeDateForComparison(oldVal) !== normalizeDateForComparison(newVal);
        }
        if (CASE_INSENSITIVE_KEYS.has(key)) {
          return oldVal.toLowerCase() !== newVal.toLowerCase();
        }
        return oldVal !== newVal;
      })
      .map(({ key }) => {
        if (ARRAY_KEYS.has(key)) {
          const toArr = (v: any) => (Array.isArray(v) ? v as string[] : String(v || '').split(',').map((s: string) => s.trim())).filter(Boolean);
          return { fieldName: key, oldValue: toArr((initial as any)[key]).join(', '), newValue: toArr((updated as any)[key]).join(', ') };
        }
        return { fieldName: key, oldValue: String((initial as any)[key] || ''), newValue: String((updated as any)[key] || '') };
      });

  // Helper function to ensure dates are in YYYY-MM-DD format (as expected by student API)
  const ensureValidDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
    
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      
      // If it's in dd/MM/yyyy format, convert to YYYY-MM-DD
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      // Try to parse as Date and format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      // If all parsing fails, return today's date as fallback
      const today = new Date();
      return today.toISOString().split('T')[0];
    } catch (error) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  };

  // Helper function to convert student data to ApplicationFormData format
  const convertStudentToApplicationForm = (student: any): ApplicationFormData => {

    // Helper function to convert various date formats to dd/MM/yyyy format
    const convertDateToDisplayFormat = (dateInput: string | number[] | null | undefined): string => {
      if (!dateInput) return '';
      
      try {
        // Handle array format dates [year, month, day] - common from APIs
        if (Array.isArray(dateInput) && dateInput.length >= 3) {
          const [year, month, day] = dateInput;
          if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
            // Note: Backend sends month as 1-indexed (1=Jan, 12=Dec), so we use it directly
            const date = new Date(year, month - 1, day);
            if (!isNaN(date.getTime())) {
              return format(date, 'dd/MM/yyyy');
            }
          }
        }
        
        // Handle string format dates
        if (typeof dateInput === 'string') {
          // If it's already in dd/MM/yyyy format, return as is
          if (dateInput.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            return dateInput;
          }
          
          // If it's in YYYY-MM-DD format, convert to dd/MM/yyyy
          if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateInput.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Try to parse as Date and format
          const date = new Date(dateInput);
          if (!isNaN(date.getTime())) {
            return format(date, 'dd/MM/yyyy');
          }
        }
        
        // If all parsing fails, return empty string (no fallback to today's date)
        return '';
      } catch (error) {
        return '';
      }
    };

    // Helper function to parse subjects from comma-separated string or array
    const parseSubjects = (subjectsData: any): string[] => {
      if (!subjectsData) return [];
      
      try {
        // If it's already an array, use it directly
        if (Array.isArray(subjectsData)) {
          return subjectsData.filter(Boolean);
        }
        
        // If it's a string, split by comma
        if (typeof subjectsData === 'string') {
          return subjectsData.split(',').map(s => s.trim()).filter(Boolean);
        }
        
        // If it's individual subject fields, extract codes
        if (subjectsData.subject1 || subjectsData.subject2 || subjectsData.subject3 || 
            subjectsData.subject4 || subjectsData.subject5 || subjectsData.subject6) {
          const subjects = [
            extractSubjectCode(subjectsData.subject1),
            extractSubjectCode(subjectsData.subject2),
            extractSubjectCode(subjectsData.subject3),
            extractSubjectCode(subjectsData.subject4),
            extractSubjectCode(subjectsData.subject5),
            extractSubjectCode(subjectsData.subject6)
          ].filter(Boolean); // Remove empty strings
          return subjects;
        }
        
        return [];
      } catch (error) {
        return [];
      }
    };

    // Helper function to extract subject code from full subject name
    // Backend sends "(411) English" but form expects "411"
    const extractSubjectCode = (fullSubjectName: string | null | undefined): string => {
      if (!fullSubjectName) return '';
      const codeMatch = fullSubjectName.match(/\((\d+)\)/);
      return codeMatch ? codeMatch[1] : fullSubjectName;
    };

    // Parse subjects from the student data
    const subjects = parseSubjects(student.subjects || student);

    return {
      id: student.id,
      applicationNo: student.applicationNo || '',
      rollNo: student.rollNumber || '',
      enrolmentNo: student.enrolmentNo || '',
      scholarNo: student.scholarNo || '',
      sssmid: student.sssmid || '',
      penNo: student.penNo || '',
      apparId: student.apparId || '', // Fixed: was using student.apparIdNo
      studentCode: student.studentCode || '', // Include studentCode field
      studentName: student.studentName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown Student',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      dateOfBirth: convertDateToDisplayFormat(student.dateOfBirth),
      gender: student.gender || '',
      religion: student.religion || '',
      caste: student.caste || '',
      subCaste: student.subCaste || '',
      casteCertificate: student.casteCertificate || '',
      aadharNo: student.aadharNo || '',
      houseNo: student.houseNo || '',
      street: student.street || '',
      landmark: student.landmark || '',
      area: student.area || '',
      city: student.village || student.city || '',
      postOffice: student.postOffice || '',
      district: student.district || '',
      state: student.state || '',
      pinCode: student.pinCode || '',
      mobileNo: student.mobileNo || student.phone || '', // Fixed: was using student.phone, added fallback
      gmail: student.gmail || student.email || '',
      bankName: student.bankName || '',
      ifcCode: student.ifcCode || '',
      accountNo: student.accountNo || '',
      annualIncome: student.annualIncome || '',
      admissionDate: convertDateToDisplayFormat(student.admissionDate),
      admissionClass: student.admissionClass || student.className || '',
      section: student.section || '',
      medium: student.medium || '',
      admissionType: student.admissionType || '',
      stream: student.stream || '', // Stream field with default empty string
      subjects: subjects, // Use the new subjects array
      schoolId: student.schoolId || schoolId || 0,
      isRegistered: 'Approved', // Since this is an existing student (approved)
      sessionId: student.sessionId || selectedSession?.id || 0,
      sessionName: selectedSession?.name || '',
      cwsnCode: student.cwsnCode || '',
      sambalKramank: student.sambalKramank || '',
      house: student.house || '',
      houseId: student.houseId || '',
      rte: student.rte || '',
    };
  };

  // Helper function to convert ApplicationFormData to ApplicationStudentDTO for update
  const convertApplicationFormToStudentDTO = (formData: ApplicationFormData): ApplicationStudentDTO => {
    // Extract class name from selectedClass (e.g., "4th-A" -> "4th")
    const getClassNameFromSelected = (selectedClass: any, admissionClass: string): string => {
      if (selectedClass?.name) {
        // If selectedClass name contains "-", take the part before it
        const classNamePart = selectedClass.name.split('-')[0];
        return classNamePart || selectedClass.name;
      }
      // Fallback to admissionClass if selectedClass is not available
      return admissionClass || '';
    };

    // Helper function to convert subject code back to full subject name
    // Form contains codes like "411", backend expects "(411) English"
    const convertSubjectCodeToFullName = (subjectCode: string, subjectType: 'language' | 'maths' | 'science'): string => {
      if (!subjectCode) return '';
      
      // Define subject mappings
      const languageSubjects: { [key: string]: string } = {
        '401': '(401) Hindi',
        '411': '(411) English',
        '512': '(512) Sanskrit',
        '508': '(508) Urdu',
        '502': '(502) Marathi',
        '504': '(504) Gujarati',
        '507': '(507) Punjabi',
        '509': '(509) Sindhi',
        '878': '(878) Electronics & Hardware'
      };
      
      const mathsSubjects: { [key: string]: string } = {
        '100': '(100) Maths (Standard)',
        '101': '(101) Maths (Basic)',
        '162': '(162) Painting',
        '163': '(163) Gayan Vadan',
        '165': '(165) Computer'
      };
      
      const scienceSubjects: { [key: string]: string } = {
        '200': '(200) Science',
        '162': '(162) Painting',
        '163': '(163) Gayan Vadan',
        '165': '(165) Computer'
      };
      
      let fullName = '';
      switch (subjectType) {
        case 'language':
          fullName = languageSubjects[subjectCode] || subjectCode;
          break;
        case 'maths':
          fullName = mathsSubjects[subjectCode] || subjectCode;
          break;
        case 'science':
          fullName = scienceSubjects[subjectCode] || subjectCode;
          break;
      }
      
      return fullName;
    };

    // Ensure studentName is not empty - this is required by backend
    const studentName = formData.studentName?.trim() || 'Unknown Student';
    if (!studentName) {
      throw new Error('Student name is required');
    }

    const normalizedAdmissionClass = (formData.admissionClass || selectedClass?.name || '').toString().trim().toLowerCase();
    const normalizedSection = (formData.section || '').toString().trim().toLowerCase();
    const allClasses = classesQuery.data || [];
    const matchedClass = allClasses.find((cls: any) =>
      (cls?.name || '').toString().trim().toLowerCase() === normalizedAdmissionClass &&
      (cls?.section || '').toString().trim().toLowerCase() === normalizedSection
    );
    const resolvedSchoolClassId =
      matchedClass?.id ||
      selectedClass?.id ||
      currentStudent?.schoolClassId ||
      1;

    return {
      applicationNo: formData.applicationNo || '',
      rollNumber: formData.rollNo || '',
      enrolmentNo: formData.enrolmentNo || '',
      studentCode: formData.studentCode || '', // Include studentCode field
      studentName: studentName, // This is the required field
      fatherName: formData.fatherName || '',
      motherName: formData.motherName || '',
      dateOfBirth: ensureValidDate(formData.dateOfBirth),
      gender: formData.gender || '',
      medium: formData.medium || 'Hindi',
      caste: formData.caste || '',
      subCaste: formData.subCaste || '',
      casteCertificate: formData.casteCertificate || '',
      religion: formData.religion || '',
      sssmid: formData.sssmid || '',
      scholarNo: formData.scholarNo || '',
      penNo: formData.penNo || '',
      apparId: formData.apparId || '',
      aadharNo: formData.aadharNo || '',
      houseNo: formData.houseNo || '',
      street: formData.street || '',
      landmark: formData.landmark || '',
      area: formData.area || '',
      city: formData.city || '',
      postOffice: formData.postOffice || '',
      district: formData.district || '',
      state: formData.state || '',
      pinCode: formData.pinCode || '',
      mobileNo: formData.mobileNo || '',
      bankName: formData.bankName || '',
      ifcCode: formData.ifcCode || '',
      accountNo: formData.accountNo || '',
      annualIncome: formData.annualIncome || '',
      section: formData.section || '',
      subjects: (() => {
        if (Array.isArray(formData.subjects)) {
          return formData.subjects;
        }
        if (typeof formData.subjects === 'string') {
          return (formData.subjects as string).split(',').filter(Boolean);
        }
        return [];
      })(), // Ensure subjects is always an array
      gmail: formData.gmail || '',
      admissionDate: ensureValidDate(formData.admissionDate),
      admissionClass: formData.admissionClass || '',
      admissionType: formData.admissionType || '',
      stream: formData.stream || '', // Stream field with default empty string
      schoolId: formData.schoolId || schoolId || 0,
      schoolClassId: resolvedSchoolClassId,
      sessionName: selectedSession?.name || '',
      sessionId: formData.sessionId || selectedSession?.id || 1,
      className: getClassNameFromSelected(selectedClass, formData.admissionClass),
      // Include photo and signature File objects if they exist (don't convert to empty string)
      studentPhoto: formData.studentPhoto || undefined,
      studentSignature: formData.studentSignature || undefined,
      // Add userId if available from the current student 
      ...(currentStudent?.userId ? { userId: currentStudent.userId } : {}),
      cwsnCode: formData.cwsnCode || '',
      sambalKramank: formData.sambalKramank || '',
      house: formData.house || '',
      houseId: formData.houseId || null,
      rte: formData.rte || '',
      status: true // Add required status field
    };
  };

  // Helper function to convert ApplicationStudentDTO to StudentFormData for the update mutation
  const convertDTOToStudentFormData = (dto: ApplicationStudentDTO): any => {
    return {
      firstName: dto.studentName.split(' ')[0] || '',
      lastName: dto.studentName.split(' ').slice(1).join(' ') || '',
      rollNumber: dto.rollNumber,
      dateOfBirth: dto.dateOfBirth,
      gender: dto.gender,
      phone: dto.mobileNo,
      email: dto.gmail,
      guardianName: dto.fatherName, // Using father name as guardian
      guardianPhone: dto.mobileNo,
      guardianEmail: dto.gmail,
      className: dto.admissionClass,
      section: dto.section,
      schoolId: dto.schoolId,
      active: true,
      applicationNo: dto.applicationNo,
      enrolmentNo: dto.enrolmentNo,
      fatherName: dto.fatherName,
      motherName: dto.motherName,
      caste: dto.caste,
      subCaste: dto.subCaste,
      casteCertificate: dto.casteCertificate,
      religion: dto.religion,
      sssmid: dto.sssmid,
      scholarNo: dto.scholarNo,
      penNo: dto.penNo,
      apparIdNo: dto.apparId,
      aadharNo: dto.aadharNo,
      houseNo: dto.houseNo,
      street: dto.street,
      landmark: dto.landmark,
      area: dto.area,
      village: dto.city,
      postOffice: dto.postOffice,
      district: dto.district,
      state: dto.state,
      pinCode: dto.pinCode,
      bankName: dto.bankName,
      ifcCode: dto.ifcCode,
      accountNo: dto.accountNo,
      annualIncome: dto.annualIncome,
      subjects: dto.subjects || [], // Use the new subjects array directly
      gmail: dto.gmail,
      admissionDate: dto.admissionDate,
      admissionClass: dto.admissionClass,
      password: '', // Keep existing password
      medium: dto.medium,
      sessionId: dto.sessionId,
      classId: selectedClass?.id || 0,
    };
  };

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentStudent(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (student: any) => {
    
    // Safely invalidate photo queries - wrap in try-catch to prevent logout on errors
    // These queries might fail with 404 if student doesn't have a photo, which is normal
    try {
      // Use removeQueries instead of invalidateQueries to avoid immediate refetch
      // The modal will fetch fresh data when it opens if needed
      queryClient.removeQueries({
        queryKey: ["studentPhotoInfo", student.id],
        exact: true
      });
      queryClient.removeQueries({
        queryKey: ["applicationPhotoInfo", student.id],
        exact: true
      });
    } catch (error) {
      // Silently handle any errors during query removal
      // This prevents logout if there's an issue with query cache
    }
    
    // Convert student to ApplicationFormData
    const convertedData = convertStudentToApplicationForm(student);
    
    // Check for empty fields
    const emptyFields = Object.entries(convertedData)
      .filter(([key, value]) => value === '' || value === null || value === undefined)
      .map(([key]) => key);
    
    
    setIsEdit(true);
    setCurrentStudent(student);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentStudent(undefined);
    setIsEdit(false);
  };

  // Create a Set of student IDs that have marksheets
  const studentsWithMarksheets = useMemo(() => {
    const marksheets = marksheetsQuery.data || [];
    return new Set(marksheets.map((marksheet: any) => marksheet.studentId || marksheet.student?.id).filter(Boolean));
  }, [marksheetsQuery.data]);

  // Check if a student has marksheets
  const hasMarksheet = (studentId: number): boolean => {
    return studentsWithMarksheets.has(studentId);
  };

  const handleOpenDeleteDialog = (id: number) => {
    // Check if student has marksheets before opening delete dialog
    if (hasMarksheet(id)) {
      showError(t('cannot_delete_student_with_marksheet'));
      return;
    }
    setStudentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (studentToDelete !== null) {
      try {
        await deleteStudentMutation.mutateAsync(studentToDelete);
        
        // Manually update the React Query cache to hide the deleted student immediately
        // This ensures the UI updates without waiting for a refetch
        if (studentsData && selectedClass?.id && selectedSession?.id) {
          // Find the student and set their status to false
          const updatedStudents = studentsData.map((student: any) => 
            student.id === studentToDelete 
              ? { ...student, status: false }
              : student
          );
          
          // Update the specific query cache that this component is using
          const queryKey = ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id];
          queryClient.setQueryData(queryKey, updatedStudents);
          
          // Also update the local state to ensure immediate re-render
          // This is a backup approach in case the cache update doesn't trigger immediately
        }
        
        // Close the dialog after successful deletion
        handleCloseDeleteDialog();
        // Show success message in top right corner snackbar
        showSuccess(t('student_deleted_successfully'));
      } catch (error: any) {
        // Show error message in top right corner snackbar
        showError(error?.message || t('failed_to_delete_student'));
      }
    }
  };

  // Download students handler - opens sort modal
  const handleDownloadStudents = () => {
    if (!selectedClass || !selectedSession || !schoolId) {
      showError(t('please_select_class_session_and_school'));
      return;
    }
    setSortModalOpen(true);
  };

  // Handle sort selection and download
  const handleSortSelection = (sortBy: "rollnumber" | "studentname") => {
    if (!selectedClass || !selectedSession || !schoolId) {
      showError(t('please_select_class_session_and_school'));
      return;
    }

    const downloadParams = {
      schoolId: schoolId,
      classId: classNameFilter ? null : selectedClass.id!,
      className: classNameFilter ? classNameFilter : null,
      sessionId: selectedSession.id!,
      sortBy: sortBy
    };

    downloadStudentsMutation.mutate(downloadParams, {
      onSuccess: () => {
        showSuccess(t('students_downloaded_successfully'));
        setSortModalOpen(false);
      },
      onError: (error: any) => {
        showError(t('download_data_sheet_failed').replace('{error}', error?.message || 'Unknown error'));
      }
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleOpenProfileStatusDialog = (student: any) => {
    // Read userStatus from fresh query data (studentsData) to get latest status from API response
    const studentFromQuery = studentsData.find((s: any) => s.id === student.id) as any;
    // Use userStatus from query data if available, otherwise fallback to student parameter
    const userStatus = studentFromQuery?.userStatus !== undefined 
      ? studentFromQuery.userStatus 
      : ((student as any)?.userStatus !== undefined ? (student as any).userStatus : true);
    // true = enabled, false = disabled
    setStudentToUpdateStatus({ id: student.id, userStatus: userStatus === true });
    setProfileStatusDialogOpen(true);
  };

  const handleCloseProfileStatusDialog = () => {
    setProfileStatusDialogOpen(false);
    setStudentToUpdateStatus(null);
  };

  const handleConfirmProfileStatusUpdate = async () => {
    if (studentToUpdateStatus !== null) {
      try {
        const response = await updateStudentUserStatusMutation.mutateAsync(studentToUpdateStatus.id);
        // Backend returns UserStatusDto: { userId, studentId, status, message }
        
        // Extract message and status from response (handle both string and object responses)
        let message: string;
        let newUserStatus: boolean | undefined;
        if (typeof response === 'string') {
          message = response;
        } else if (response && typeof response === 'object') {
          const responseObj = response as any;
          message = responseObj?.message || responseObj?.data || JSON.stringify(response);
          // Read userStatus (or status) from response (UserStatusDto)
          if (typeof responseObj.userStatus === 'boolean') {
            newUserStatus = responseObj.userStatus;
          } else if (typeof responseObj.status === 'boolean') {
            newUserStatus = responseObj.status;
          }
        } else {
          message = String(response);
        }
        
        // Update the query cache with the new userStatus from response
        if (newUserStatus !== undefined && selectedSession?.id && selectedClass?.id) {
          const queryKey = ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id];
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((student: any) =>
              student.id === studentToUpdateStatus.id
                ? { ...student, userStatus: newUserStatus }
                : student
            );
          });
        }
        
        // Display the exact message from backend
        showSuccess(message || t('student_profile_status_updated') || 'Student profile status updated');
        handleCloseProfileStatusDialog();
        handleMenuClose();
        // Refetch students to update the UI (as fallback if cache update fails)
        refetch();
      } catch (error: any) {
        showError(error?.message || t('failed_to_update_student_profile_status') || 'Failed to update student profile status');
      }
    }
  };

  const handleApplicationFormSubmit = (formData: ApplicationFormData) => {

    if (isEdit && currentStudent?.id) {
      // Teacher flow: show confirmation popup and send update request
      if (isTeacher()) {
        const initialData = convertStudentToApplicationForm(currentStudent);
        const changedFields = getChangedTeacherFields(initialData, formData);
        if (changedFields.length === 0) {
          showError('Please first make changes in the student details then only submit.');
          return;
        }
        setPendingTeacherFormData(formData);
        setTeacherConfirmOpen(true);
        return;
      }

      try {
        // Validate required fields first
        if (!formData.studentName?.trim()) {
          showError(t('student_name_required'));
          return;
        }

        if (!formData.fatherName?.trim()) {
          showError(t('father_name_required'));
          return;
        }

        if (!formData.motherName?.trim()) {
          showError(t('mother_name_required'));
          return;
        }

        if (!formData.dateOfBirth?.trim()) {
          showError(t('date_of_birth_required'));
          return;
        }

        if (!formData.gender?.trim()) {
          showError(t('gender_required'));
          return;
        }

        // Convert ApplicationFormData to ApplicationStudentDTO
        const studentDTO = convertApplicationFormToStudentDTO(formData);
        
        // Use the DTO directly with the new mutation
        
        updateStudentWithDTOMutation.mutate(
          { id: currentStudent.id, data: studentDTO },
          {
            onSuccess: () => {
              // Comprehensive query invalidation after student update
              // Invalidate student class mapping queries
              queryClient.invalidateQueries({
                queryKey: ["studentClassMapping"]
              });
              queryClient.invalidateQueries({
                queryKey: ["studentClassMapping", "class", selectedClass?.id, "session", selectedSession?.id]
              });
              
              // Invalidate all student queries
              queryClient.invalidateQueries({
                queryKey: ["students"]
              });
              queryClient.invalidateQueries({
                queryKey: ["students", schoolId]
              });
              
              // Invalidate students by sessionId and classId query to refresh the list
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.invalidateQueries({ 
                  queryKey: ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id] 
                });
              }
              
              // Invalidate current student query (for student profile)
              queryClient.invalidateQueries({
                queryKey: ["currentStudent", currentStudent.id]
              });
              
              // Invalidate photo-related queries
              queryClient.invalidateQueries({
                queryKey: ["studentPhotoInfo", currentStudent.id]
              });
              queryClient.invalidateQueries({
                queryKey: ["applicationPhotoInfo", currentStudent.id]
              });
              
              // Invalidate application forms queries (in case subjects changed)
              queryClient.invalidateQueries({
                queryKey: ["applicationForms"]
              });
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.invalidateQueries({ 
                  queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, selectedClass.id] 
                });
              }
              if (schoolId) {
                queryClient.invalidateQueries({
                  queryKey: ["applicationFormsBySchoolId", schoolId]
                });
              }
              
              // Invalidate subjects-related queries (in case subjects changed)
              queryClient.invalidateQueries({
                queryKey: ["subjects"]
              });
              
              // Refetch students list to ensure UI is updated
              refetch();
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.refetchQueries({
                  queryKey: ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id],
                  exact: true,
                });
              }
              
              setModalOpen(false);
              showSuccess(t('student_updated_successfully'));
            },
            onError: (error: any) => {
              // Extract error message from backend response
              const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || t("failed_to_update_student");
              showError(errorMessage);
            }
          }
        );
      } catch (error) {
        showError(t('error_processing_form_data'));
      }
    }
  };

  const handleTeacherConfirmSendRequest = async () => {
    if (!pendingTeacherFormData || !currentStudent?.id) return;
    const initialData = convertStudentToApplicationForm(currentStudent);
    const changedFields = getChangedTeacherFields(initialData, pendingTeacherFormData);
    try {
      await createStudentUpdateRequestMutation.mutateAsync({
        studentId: currentStudent.id,
        changes: changedFields.map(({ fieldName, oldValue, newValue }) => ({ fieldName, oldValue, newValue })),
        requestedType: 'TEACHER',
      });
      showSuccess('Request sent successfully.');
      setTeacherConfirmOpen(false);
      setModalOpen(false);
      setPendingTeacherFormData(null);
    } catch (error: any) {
      showError(error?.response?.data?.message || error?.message || 'Failed to send request');
    }
  };

  const columns = [
    { id: 'studentName', label: t('student_name'), minWidth: 200 },
    { id: 'rollNumber', label: t('roll_number'), minWidth: 100 },
    { id: 'mobileNo', label: t('mobile_number'), minWidth: 100 },
    { id: 'className', label: t('class'), minWidth: 100 },
    { id: 'section', label: t('section'), minWidth: 100 },
  ];

  let infoMessage = '';
  if (!selectedClass && !selectedSession) infoMessage = t('please_select_class_and_session');
  else if (!selectedClass) infoMessage = t('please_select_class');
  else if (!selectedSession) infoMessage = t('please_select_session');

  const classSessionStudentsForAdvanceSearch = useMemo(() => {
    return (studentsQuery.data || [])
      .filter((student: any) => {
        if (student.status !== true) return false;
        if (schoolId && student.schoolId !== schoolId) return false;
        if (selectedClass?.section && selectedClass.section !== "__GRADE_ALL__") {
          const studentSection = (student.section || "").toString().trim().toLowerCase();
          const selectedSection = selectedClass.section.toString().trim().toLowerCase();
          if (studentSection !== selectedSection) return false;
        }
        if (selectedMedium && selectedMedium.trim() !== "" && selectedMedium !== "ALL") {
          const studentMedium = student.medium;
          if (!studentMedium) return true;
          if (typeof studentMedium === 'string') {
            if (!studentMedium.toLowerCase().includes(selectedMedium.toLowerCase())) return false;
          } else if (Array.isArray(studentMedium)) {
            if (!studentMedium.some((m: string) => m.toLowerCase() === selectedMedium.toLowerCase())) return false;
          }
        }
        return true;
      })
      .sort((a: any, b: any) => {
        const nameA = (a.studentName || `${a.firstName || ''} ${a.lastName || ''}`.trim()).toLowerCase();
        const nameB = (b.studentName || `${b.firstName || ''} ${b.lastName || ''}`.trim()).toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [studentsQuery.data, selectedClass, selectedMedium, schoolId]);

  // Filter students to only show those with status: true, from current school, and matching selected medium
  // Use the new API data instead of the old one
  // Note: API already filters by sessionId and classId, so we only need to filter by status, schoolId, and medium
  const studentsData = ageFilteredStudents ?? (studentsQuery.data || []);
  const filteredStudents = (studentsData || [])
    .filter((student: any) => {
      // Basic filters - only filter by status and schoolId
      // Note: sessionId and classId filtering is already done by the API
      if (student.status !== true) {
        return false;
      }
      
      // Optional: Filter by schoolId if needed (but API should already handle this)
      if (schoolId && student.schoolId !== schoolId) {
        return false;
      }

      // Keep the table aligned with selected class section (e.g. 9th-A shows only A).
      if (selectedClass?.section && selectedClass.section !== "__GRADE_ALL__") {
        const studentSection = (student.section || "").toString().trim().toLowerCase();
        const selectedSection = selectedClass.section.toString().trim().toLowerCase();
        if (studentSection !== selectedSection) {
          return false;
        }
      }
      
      // Medium filter - only filter if medium is explicitly set and not "ALL"
      if (selectedMedium && selectedMedium.trim() !== "" && selectedMedium !== "ALL") {
        const studentMedium = student.medium;
        if (!studentMedium) {
          // If student has no medium, include it (don't exclude)
          return true;
        }
        if (typeof studentMedium === 'string') {
          // If medium is a string, check if it contains the selected medium
          if (!studentMedium.toLowerCase().includes(selectedMedium.toLowerCase())) {
            return false;
          }
        } else if (Array.isArray(studentMedium)) {
          // If medium is an array, check if it includes the selected medium
          if (!studentMedium.some((m: string) => m.toLowerCase() === selectedMedium.toLowerCase())) {
            return false;
          }
        } else {
          // Unknown format, include it
          return true;
        }
      }
      
      return true;
    })
    .sort((a: any, b: any) => {
      // Sort by student name alphabetically (A-Z)
      const nameA = (a.studentName || `${a.firstName || ''} ${a.lastName || ''}`.trim()).toLowerCase();
      const nameB = (b.studentName || `${b.firstName || ''} ${b.lastName || ''}`.trim()).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
  const formattedData = filteredStudents.map((row: any, index: number) => ({
    ...row,
    studentName: row.studentName || `${row.firstName || ''} ${row.lastName || ''}`.trim(),
    mobileNo: row.mobileNo || row.phone || '', // Normalize mobile number for search
    phone: row.phone || row.mobileNo || '', // Include phone as well for search
  }));

  const renderActions = (row: any) => (
    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
      <MoreVertIcon />
    </IconButton>
  );

  if (tenantLoading || !schoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  const parseRequestedAt = (val: any): string => {
    if (!val) return '-';
    if (Array.isArray(val) && val.length >= 3) {
      const d = new Date(val[0], val[1] - 1, val[2]);
      return d.toLocaleDateString();
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
  };

  const getStudentNameById = (id: number | undefined): string => {
    if (!id) return '-';
    const student = studentsData.find((s: any) => s.id === id);
    return student?.studentName || `Student #${id}`;
  };

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('student_management')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {ageFilteredStudents !== null && (
              <Chip
                label={`Age: ${minAge}–${maxAge} yrs (${ageFilteredStudents.length})`}
                onDelete={handleClearAgeFilter}
                color="info"
                size="small"
              />
            )}
            <Button
              variant="outlined"
              sx={{ borderColor: '#3092e3', color: '#3092e3', '&:hover': { borderColor: '#1976d2', backgroundColor: 'rgba(48,146,227,0.04)' } }}
              onClick={() => {
                setAdvanceSearchResult(null);
                setAdvanceSearchSelectedStudent(null);
                setAdvanceSearchDialogOpen(true);
              }}
              disabled={!selectedClass || !selectedSession}
            >
              Advance Search
            </Button>
            <Button
              variant="outlined"
              sx={{ borderColor: '#3092e3', color: '#3092e3', '&:hover': { borderColor: '#1976d2', backgroundColor: 'rgba(48,146,227,0.04)' } }}
              onClick={() => setAgeFilterDialogOpen(true)}
              disabled={!selectedClass || !selectedSession}
            >
              Age Filter
            </Button>
            <Button
              variant="outlined"
              sx={{ borderColor: '#3092e3', color: '#3092e3', '&:hover': { borderColor: '#1976d2', backgroundColor: 'rgba(48,146,227,0.04)' } }}
              onClick={() => setSiblingListOpen(true)}
              disabled={tenantLoading}
            >
              Sibling List
            </Button>
            {(isSchoolAdmin() || isTeacher()) && (
              <Button
                variant="outlined"
                sx={{
                  borderColor: "#3092e3",
                  color: "#3092e3",
                  "&:hover": {
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(48, 146, 227, 0.04)",
                  }
                }}
                onClick={() => setScholarNumberModalOpen(true)}
                startIcon={<AddIcon />}
              >
                {t('add_scholar_number') || 'Add Scholar Number'}
              </Button>
            )}
            <Button
              variant="contained"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleDownloadStudents}
              disabled={downloadStudentsMutation.isLoading || !selectedClass || !selectedSession || !schoolId}
              startIcon={downloadStudentsMutation.isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            >
              {downloadStudentsMutation.isLoading ? t('downloading') : t('data_sheet')}
            </Button>
            {isTeacher() && (
              <Button
                variant="outlined"
                sx={{ borderColor: '#7b1fa2', color: '#7b1fa2', '&:hover': { borderColor: '#6a1b9a', backgroundColor: 'rgba(123,31,162,0.04)' } }}
                onClick={() => setSentRequestsOpen(true)}
              >
                Sent Requests
              </Button>
            )}
          </Box>
        </Box>
        {infoMessage ? (
          <Alert severity="info" sx={{ mb: 2 }}>{infoMessage}</Alert>
        ) : isLoading ? (
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>
        ) : (
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={['studentName', 'rollNumber', 'className', 'section', 'mobileNo', 'phone']}
            filterFields={[]}
            cardProps={{
              elevation: 0,
              sx: { 
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            }}
            actions={renderActions}
          />
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              if (menuRow) handleOpenEditModal(menuRow);
              handleMenuClose();
            }}
          >
            {t('edit')}
          </MenuItem>
          <Tooltip
            title={menuRow && hasMarksheet(menuRow.id) ? t('cannot_delete_student_with_marksheet_tooltip') : ''}
            placement="left"
            arrow
          >
            <span>
              <MenuItem
                onClick={() => {
                  if (menuRow) handleOpenDeleteDialog(menuRow.id);
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
                disabled={deleteStudentMutation.isLoading || (menuRow && hasMarksheet(menuRow.id))}
              >
                {deleteStudentMutation.isLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('deleting')}
                  </>
                ) : (
                  t('delete')
                )}
              </MenuItem>
            </span>
          </Tooltip>
          <MenuItem
            onClick={() => {
              if (menuRow) {
                handleOpenProfileStatusDialog(menuRow);
                handleMenuClose();
              }
            }}
            disabled={updateStudentUserStatusMutation.isLoading}
            sx={{ color: 'warning.main' }}
          >
            {updateStudentUserStatusMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('updating') || 'Updating...'}
              </>
            ) : (() => {
              // Read userStatus from fresh query data (studentsData) to get latest status from API response
              const studentFromQuery = studentsData.find((s: any) => s.id === menuRow?.id) as any;
              // Use userStatus from query data if available, otherwise fallback to menuRow
              const userStatus = studentFromQuery?.userStatus !== undefined 
                ? studentFromQuery.userStatus 
                : ((menuRow as any)?.userStatus !== undefined ? (menuRow as any).userStatus : true);
              // true = enabled (show "Disable Student"), false = disabled (show "Enable Student")
              return userStatus === true
                ? t('disable_student') || 'Disable Student'
                : t('enable_student') || 'Enable Student';
            })()}
          </MenuItem>
        </Menu>
      </Paper>

      {/* ApplicationFormModal for editing students */}
      <ApplicationFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        initialData={currentStudent ? convertStudentToApplicationForm(currentStudent) : undefined}
        onSubmit={handleApplicationFormSubmit}
        isEdit={isEdit}
        isStudentEdit={true} // This indicates it's being used for student editing
        schoolData={{
          nameofSchool: school?.nameofSchool || '',
          blockName: school?.blockName || '',
          district: school?.district || '',
          diseCode: school?.diseCode || '',
          state: school?.state || '',
          pinNo: school?.pinNo || '',
          schoolCode: school?.schoolCode || '',
          schoolLogo: school?.schoolLogo || '',
          medium: (() => {
            const schoolMedium = school?.medium;
            return Array.isArray(schoolMedium) 
              ? schoolMedium.join(', ') 
              : schoolMedium || '';
          })(),
        }}
        selectedClass={selectedClass}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('confirm_delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('are_you_sure_you_want_to_delete_this_student')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            autoFocus
            disabled={deleteStudentMutation.isLoading}
          >
            {deleteStudentMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('deleting')}
              </>
            ) : (
              t('delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={profileStatusDialogOpen}
        onClose={handleCloseProfileStatusDialog}
        aria-labelledby="profile-status-dialog-title"
        aria-describedby="profile-status-dialog-description"
      >
        <DialogTitle id="profile-status-dialog-title">
          {studentToUpdateStatus?.userStatus
            ? t("confirm_disable_profile") || "Confirm Disable Profile"
            : t("confirm_enable_profile") || "Confirm Enable Profile"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="profile-status-dialog-description">
            {studentToUpdateStatus?.userStatus
              ? t("are_you_sure_you_want_to_disable_student_profile_and_login") || "Are you sure you want to disable student profile and login?"
              : t("are_you_sure_you_want_to_enable_student_profile_and_login") || "Are you sure you want to enable student profile and login?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileStatusDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmProfileStatusUpdate}
            color={studentToUpdateStatus?.userStatus ? "warning" : "success"}
            autoFocus
            disabled={updateStudentUserStatusMutation.isLoading}
          >
            {updateStudentUserStatusMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('updating') || 'Updating...'}
              </>
            ) : (
              studentToUpdateStatus?.userStatus
                ? t("disable_login") || "Disable Login"
                : t("enable_login") || "Enable Login"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <StudentDataSheetSortModal
        open={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
        onSelectSortBy={handleSortSelection}
        isLoading={downloadStudentsMutation.isLoading}
      />

      <ScholarNumberModal
        open={scholarNumberModalOpen}
        handleClose={() => setScholarNumberModalOpen(false)}
        onSubmit={async (students: StudentRollScholarUpdateDto[]) => {
          try {
            await updateRollNumberAndScholarNumberMutation.mutateAsync(students);
            showSuccess(t("scholar_numbers_updated_successfully") || "Scholar numbers updated successfully");
            setScholarNumberModalOpen(false);
            refetch();
          } catch (error: any) {
            showError(
              error?.response?.data?.message ||
                t("error_updating_scholar_numbers") ||
                "Error updating scholar numbers"
            );
          }
        }}
      />

      {/* Sent Requests Drawer */}
      <Drawer
        anchor="right"
        open={sentRequestsOpen}
        onClose={() => { setSentRequestsOpen(false); setExpandedRequestId(null); }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2, borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
          <Typography variant="h6" fontWeight="bold">Sent Requests</Typography>
          <IconButton onClick={() => { setSentRequestsOpen(false); setExpandedRequestId(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {sentRequestsLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : !sentRequests || sentRequests.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>No requests found.</Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fields</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedSentRequests.map((req: any) => {
                    const changes: any[] = req.changes || [];
                    const isExpanded = expandedRequestId === req.studentId;
                    return (
                      <React.Fragment key={req.studentId}>
                        <TableRow hover sx={{ cursor: changes.length > 0 ? 'pointer' : 'default' }} onClick={() => setExpandedRequestId(isExpanded ? null : req.studentId)}>
                          <TableCell>{getStudentNameById(req.studentId)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="primary" sx={{ textDecoration: 'underline', display: 'inline' }}>
                              {changes.length} field{changes.length !== 1 ? 's' : ''} changed
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={req.status || 'PENDING'} color={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'error' : 'warning'} size="small" />
                          </TableCell>
                          <TableCell>{parseRequestedAt(req.requestedAt)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} unmountOnExit>
                              <Box sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                                {changes.map((change: any) => (
                                  <Box key={change.fieldName} sx={{ display: 'flex', gap: 2, mb: 0.5, fontSize: '0.875rem' }}>
                                    <span style={{ fontWeight: 600, minWidth: 140 }}>{change.fieldName}</span>
                                    <span style={{ color: '#c62828' }}>{change.oldValue || '-'}</span>
                                    <span>→</span>
                                    <span style={{ color: '#2e7d32' }}>{change.newValue || '-'}</span>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Drawer>

      {/* Advance Search Dialog */}
      <Dialog open={advanceSearchDialogOpen} onClose={handleCloseAdvanceSearch} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Advance Search
          <IconButton onClick={handleCloseAdvanceSearch} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 420 }}>
          <Box sx={{ width: { xs: '100%', md: advanceSearchSelectedStudent ? 200 : 280 }, flexShrink: 0 }}>
            {advanceSearchSelectedStudent ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearAdvanceSelection}
                sx={{
                  borderColor: '#3092e3',
                  color: '#3092e3',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#1976d2', backgroundColor: 'rgba(48,146,227,0.04)' },
                }}
              >
                Select Other Student
              </Button>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Select Student
                </Typography>
                {isLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : classSessionStudentsForAdvanceSearch.length === 0 ? (
                  <Alert severity="info">No students found.</Alert>
                ) : (
                  <List
                    sx={{
                      maxHeight: 360,
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: '#fff',
                    }}
                  >
                    {classSessionStudentsForAdvanceSearch.map((student: any) => {
                      const name = student.studentName || '-';
                      return (
                        <ListItemButton
                          key={student.id}
                          onClick={() => handleAdvanceSearchSelect(student)}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: '#3092e3', width: 36, height: 36, fontSize: '1rem', fontWeight: 700 }}>
                              {getStudentInitial(name)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={name.toUpperCase()}
                            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {advanceSearchLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={32} />
              </Box>
            )}
            {!advanceSearchLoading && !advanceSearchResult && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <Typography color="text.secondary">Select a student to view details</Typography>
              </Box>
            )}
            {advanceSearchResult && !advanceSearchLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Student Details
                  </Typography>
                  <EnhancedTable
                    columns={[
                      { id: 'studentName', label: t('student_name'), minWidth: 160 },
                      { id: 'rollNumber', label: t('roll_number'), minWidth: 120 },
                      { id: 'mobileNo', label: t('mobile_number'), minWidth: 110 },
                      { id: 'className', label: t('class'), minWidth: 80 },
                      { id: 'section', label: t('section'), minWidth: 70 },
                    ]}
                    data={[{
                      ...advanceSearchResult.studentDto,
                      mobileNo: advanceSearchResult.studentDto?.mobileNo || '',
                    }]}
                    searchFields={[]}
                    filterFields={[]}
                    enablePagination={false}
                    cardProps={{ elevation: 0, sx: { border: 'none', boxShadow: 'none' } }}
                    actions={() => (
                      <Button
                        variant="contained"
                        size="small"
                        sx={advanceViewButtonSx}
                        onClick={() => setAdvanceViewApplicationOpen(true)}
                      >
                        {t('view')}
                      </Button>
                    )}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Marksheets
                  </Typography>
                  <EnhancedTable
                    columns={[
                      { id: 'marksheetType', label: t('marksheet_type') || 'Type', minWidth: 120 },
                      { id: 'className', label: t('class'), minWidth: 80 },
                      { id: 'percentage', label: t('percentage') || '%', minWidth: 70 },
                      { id: 'result', label: t('result') || 'Result', minWidth: 80 },
                    ]}
                    data={advanceSearchResult.marksheetResponseDtoList || []}
                    searchFields={[]}
                    filterFields={[]}
                    enablePagination={false}
                    cardProps={{ elevation: 0, sx: { border: 'none', boxShadow: 'none' } }}
                    actions={(row) => (
                      <Button
                        variant="contained"
                        size="small"
                        sx={advanceViewButtonSx}
                        onClick={() => setAdvanceViewMarksheet(row)}
                      >
                        {t('view')}
                      </Button>
                    )}
                  />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Fee Receipts
                  </Typography>
                  <EnhancedTable
                    columns={[
                      {
                        id: 'receiptDate',
                        label: t('date') || 'Date',
                        minWidth: 110,
                        format: (_: unknown, row: any) => parseRequestedAt(row?.localDateTime),
                      },
                      {
                        id: 'amount',
                        label: t('amount') || 'Amount',
                        minWidth: 90,
                        format: (_: unknown, row: any) => {
                          const amt = row?.feeTransactionDto?.amount ?? 0;
                          return `₹${Number(amt).toLocaleString()}`;
                        },
                      },
                      {
                        id: 'paymentType',
                        label: t('payment_type') || 'Payment Type',
                        minWidth: 110,
                        format: (_: unknown, row: any) => row?.feeTransactionDto?.paymentType || '-',
                      },
                    ]}
                    data={(advanceSearchResult.feeReceiptResponseDtoList || []).map((r) => ({
                      ...r,
                      receiptDate: r.localDateTime,
                    }))}
                    searchFields={[]}
                    filterFields={[]}
                    enablePagination={false}
                    cardProps={{ elevation: 0, sx: { border: 'none', boxShadow: 'none' } }}
                    actions={(row) => (
                      <Button
                        variant="contained"
                        size="small"
                        sx={advanceViewButtonSx}
                        onClick={() => handleAdvanceViewFeeReceipt(row)}
                      >
                        {t('view')}
                      </Button>
                    )}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={advanceViewApplicationOpen}
        onClose={() => setAdvanceViewApplicationOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('admission_form_preview') || 'Application Form'}</span>
            <IconButton onClick={() => setAdvanceViewApplicationOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {advanceSearchResult?.studentDto && (
            <ApplicationFormTemplate
              key={`advance-view-${advanceSearchResult.studentDto.id}-${advanceViewApplicationOpen}`}
              data={{
                ...convertStudentToApplicationForm(advanceSearchResult.studentDto),
                studentId: advanceSearchResult.studentDto.id,
              }}
              schoolData={{
                nameofSchool: school?.nameofSchool || '',
                blockName: school?.blockName || '',
                district: school?.district || '',
                diseCode: school?.diseCode || '',
                state: school?.state || '',
                pinNo: school?.pinNo || '',
                schoolCode: school?.schoolCode || '',
                schoolLogo: school?.schoolLogo || '',
                medium: (() => {
                  const schoolMedium = school?.medium;
                  return Array.isArray(schoolMedium) ? schoolMedium.join(', ') : schoolMedium || '';
                })(),
              }}
              renderForPDF={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!advanceViewMarksheet}
        onClose={() => setAdvanceViewMarksheet(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh', height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('view_marksheet') || 'View Marksheet'} — {advanceViewMarksheet?.student?.studentName || ''}
          <IconButton onClick={() => setAdvanceViewMarksheet(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {advanceViewMarksheet && (
            <MarksheetTemplate
              student={advanceViewMarksheet.student as any}
              schoolName={school?.nameofSchool || advanceViewMarksheet.schoolName || ''}
              schoolCode={school?.schoolCode || ''}
              diseCode={school?.diseCode || ''}
              marksheetType={advanceViewMarksheet.marksheetType}
              sessionYear={advanceViewMarksheet.sessionYear}
              className={advanceViewMarksheet.className}
              subjects={(advanceViewMarksheet.subjects || []) as any}
              percentage={advanceViewMarksheet.percentage}
              totalGrade={String(advanceViewMarksheet.totalGrade || '')}
              result={advanceViewMarksheet.result}
              rank={advanceViewMarksheet.rank}
              failSubjects={advanceViewMarksheet.failSubjects || ''}
              teacherId={teachersQuery.data?.[0]?.id}
              classId={advanceViewMarksheet.classId}
              sessionId={advanceViewMarksheet.sessionId}
              school={{
                nameofSchool: school?.nameofSchool,
                schoolCode: school?.schoolCode,
                diseCode: school?.diseCode,
                blockName: school?.blockName,
                district: school?.district,
                state: school?.state,
                schoolLogo: school?.schoolLogo,
              } as any}
              hideDownloadButton={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!advanceViewFeeReceipt} onClose={() => setAdvanceViewFeeReceipt(null)} fullScreen>
        {advanceViewFeeReceipt && (
          <FeeReceiptTemplate
            receipt={advanceViewFeeReceipt}
            onBack={() => setAdvanceViewFeeReceipt(null)}
          />
        )}
      </Dialog>

      {/* Age Filter Dialog */}
      <Dialog open={ageFilterDialogOpen} onClose={() => setAgeFilterDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Filter Students by Age</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Min Age"
            type="number"
            value={minAge}
            onChange={(e) => setMinAge(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
          <TextField
            label="Max Age"
            type="number"
            value={maxAge}
            onChange={(e) => setMaxAge(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setAgeFilterDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAgeFilterSubmit}
            disabled={!minAge || getAgeWiseStudentsMutation.isLoading}
            sx={{ backgroundColor: '#3092e3', '&:hover': { backgroundColor: '#2573c1' } }}
          >
            {getAgeWiseStudentsMutation.isLoading ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teacher: send student profile change request confirmation */}
      <Dialog open={teacherConfirmOpen} onClose={() => setTeacherConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Send Request to Principal
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send student profile change request to Principal?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setTeacherConfirmOpen(false)}
            disabled={createStudentUpdateRequestMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTeacherConfirmSendRequest}
            disabled={createStudentUpdateRequestMutation.isLoading}
            sx={{ backgroundColor: '#3092e3', '&:hover': { backgroundColor: '#2573c1' } }}
          >
            {createStudentUpdateRequestMutation.isLoading ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sibling List Dialog */}
      <Dialog open={siblingListOpen} onClose={() => setSiblingListOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('sibling_list') || 'Student Sibling List'}
          </Typography>
          <IconButton onClick={() => setSiblingListOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'center' } }}>
            <TextField
              label={t('search_placeholder') || "Search by Name or Mobile..."}
              variant="outlined"
              size="small"
              value={siblingSearchQuery}
              onChange={(e) => setSiblingSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            {selectedClass && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="checkbox"
                  id="showCurrentClassOnly"
                  checked={showCurrentClassOnly}
                  onChange={(e) => setShowCurrentClassOnly(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="showCurrentClassOnly" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                  Show current class ({selectedClass.name}) only
                </label>
              </Box>
            )}
          </Box>

          {siblingGroupsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : siblingTableRows.length === 0 ? (
            <Alert severity="info">{t('no_siblings_found') || 'No sibling mappings found.'}</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400, border: '1px solid #e2e8f0' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Student Name (Left)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Class & Section</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Sibling Name (Right)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Sibling Class & Section</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Login Account</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8fafc' }}>Parent Contact</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {siblingTableRows.map((row, index) => (
                    <TableRow key={`${row.studentId}-${index}`} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{row.studentName}</TableCell>
                      <TableCell>{`${row.className} - ${row.section}`}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {row.siblingsList.map((sib, sIdx) => (
                            <Typography key={sIdx} variant="body2" sx={{ fontWeight: 500 }}>
                              {sib.studentName}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {row.siblingsList.map((sib, sIdx) => (
                            <Typography key={sIdx} variant="body2" color="text.secondary">
                              {`${sib.className} - ${sib.section}`}
                            </Typography>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.loginAccountName}</TableCell>
                      <TableCell color="text.secondary">{row.parentMobile || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setSiblingListOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentManagement;